import type { ColorPalette } from '@/config/customizerConfig';

const FALLBACK_PALETTES: ColorPalette[] = [
  {
    id: 'ivory_obsidian',
    name: 'ivory_obsidian',
    displayName: 'Ivory & Obsidian',
    description: 'Classic black baseboard with warm ivory cream map and luxurious gold text. Timeless sophistication.',
    colors: { mapColor: '#D6C5A9', baseBoardColor: '#E2D4C7', textColor: '#A08040' },
    renderImage: '/images/palettes/01-ivory-obsidian.webp',
  },
  {
    id: 'ocean_teal',
    name: 'ocean_teal',
    displayName: 'Ocean Teal',
    description: 'Mediterranean turquoise with crisp white map. Fresh coastal elegance.',
    colors: { mapColor: '#E3E2DD', baseBoardColor: '#4F8686', textColor: '#F0F0F0' },
    renderImage: '/images/palettes/02-ocean-teal.webp',
  },
  {
    id: 'navy_classic',
    name: 'navy_classic',
    displayName: 'Navy Classic',
    description: 'Deep navy blue with clean white. Formal, nautical refinement.',
    colors: { mapColor: '#DBD9D4', baseBoardColor: '#1C2841', textColor: '#E0E0E0' },
    renderImage: '/images/palettes/03-navy-classic.webp',
  },
  {
    id: 'charcoal_modern',
    name: 'charcoal_modern',
    displayName: 'Charcoal Modern',
    description: 'Contemporary charcoal with warm gray tones. Understated modern design.',
    colors: { mapColor: '#A89A8E', baseBoardColor: '#443C35', textColor: '#EDE3D7' },
    renderImage: '/images/palettes/04-charcoal-modern.webp',
  },
  {
    id: 'sage_cream',
    name: 'sage_cream',
    displayName: 'Sage & Cream',
    description: 'Natural sage green on warm cream. Organic, botanical, serene.',
    colors: { mapColor: '#9EAA8F', baseBoardColor: '#F0E9DA', textColor: '#465A3F' },
    renderImage: '/images/palettes/05-sage-cream.webp',
    isDefault: true,
  },
  {
    id: 'sand_walnut',
    name: 'sand_walnut',
    displayName: 'Sand & Walnut',
    description: 'Rich walnut brown with warm sand. Earthy natural warmth.',
    colors: { mapColor: '#C8A678', baseBoardColor: '#E2D6C5', textColor: '#806040' },
    renderImage: '/images/palettes/06-sand-walnut.webp',
  },
  {
    id: 'midnight_gold',
    name: 'midnight_gold',
    displayName: 'Midnight Gold',
    description: 'Dark luxury with gold accents. Dramatic premium statement.',
    colors: { mapColor: '#202A36', baseBoardColor: '#1A2430', textColor: '#B0A060' },
    renderImage: '/images/palettes/07-midnight-gold.webp',
  },
  {
    id: 'warm_slate',
    name: 'warm_slate',
    displayName: 'Warm Slate',
    description: 'Sophisticated warm gray. Architectural, neutral elegance.',
    colors: { mapColor: '#D9CBB1', baseBoardColor: '#D5CAB8', textColor: '#4F463B' },
    renderImage: '/images/palettes/08-warm-slate.webp',
  },
  {
    id: 'sakura_bloom',
    name: 'sakura_bloom',
    displayName: 'Sakura Bloom',
    description: 'Soft cherry blossom pink. Delicate, romantic, feminine.',
    colors: { mapColor: '#E3ABB1', baseBoardColor: '#E7DAD8', textColor: '#C77F87' },
    renderImage: '/images/palettes/09-sakura-bloom.webp',
  },
  {
    id: 'blush_coral',
    name: 'blush_coral',
    displayName: 'Blush Coral',
    description: 'Warm coral salmon tones. Inviting sunset warmth.',
    colors: { mapColor: '#D87A58', baseBoardColor: '#EFDAC9', textColor: '#B37362' },
    renderImage: '/images/palettes/10-blush-coral.webp',
  },
  {
    id: 'lavender_sky',
    name: 'lavender_sky',
    displayName: 'Lavender Sky',
    description: 'Soft muted lavender. Dreamy, calming, ethereal.',
    colors: { mapColor: '#C6B2CE', baseBoardColor: '#D7CBD9', textColor: '#62516E' },
    renderImage: '/images/palettes/11-lavender-sky.webp',
  },
  {
    id: 'honey_cream',
    name: 'honey_cream',
    displayName: 'Honey & Cream',
    description: 'Golden honey on warm cream. Luxurious golden warmth.',
    colors: { mapColor: '#DAB066', baseBoardColor: '#EDDFC5', textColor: '#9A7337' },
    renderImage: '/images/palettes/12-honey-cream.webp',
  },
  {
    id: 'sky_breeze',
    name: 'sky_breeze',
    displayName: 'Sky Breeze',
    description: 'Soft sky blue with navy text. Calm, serene, open air.',
    colors: { mapColor: '#AAC9DF', baseBoardColor: '#E8E3DD', textColor: '#405060' },
    renderImage: '/images/palettes/13-sky-breeze.webp',
  },
  {
    id: 'arctic_fog',
    name: 'arctic_fog',
    displayName: 'Arctic Fog',
    description: 'Cool fog gray with pure white. Minimal Nordic aesthetic.',
    colors: { mapColor: '#E3E3E3', baseBoardColor: '#E0DFE0', textColor: '#E2E2E1' },
    renderImage: '/images/palettes/14-arctic-fog.webp',
  },
  {
    id: 'mint_fresh',
    name: 'mint_fresh',
    displayName: 'Mint Fresh',
    description: 'Fresh spearmint green. Clean, tropical, vibrant.',
    colors: { mapColor: '#DFE9E3', baseBoardColor: '#DDE3DF', textColor: '#ECEFEA' },
    renderImage: '/images/palettes/15-mint-fresh.webp',
  },
  {
    id: 'steel_minimal',
    name: 'steel_minimal',
    displayName: 'Steel Minimal',
    description: 'Steel blue-gray with white. Industrial, clean lines.',
    colors: { mapColor: '#E5E6E1', baseBoardColor: '#586673', textColor: '#F0F0F0' },
    renderImage: '/images/palettes/16-steel-minimal.webp',
  },
];

let cachedPalettes: ColorPalette[] | null = null;

/**
 * Fetch color palettes from backend API. Cached for the session.
 * Falls back to hardcoded palette list if API is unavailable.
 */
export async function fetchPalettes(): Promise<ColorPalette[]> {
  if (cachedPalettes) return cachedPalettes;

  const apiUrl = import.meta.env.VITE_API_BASE_URL;

  if (!apiUrl) {
    console.warn('[PaletteService] API URL not configured, using fallback palettes');
    return FALLBACK_PALETTES;
  }

  try {
    const response = await fetch(`${apiUrl}/v1/palettes`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();

    if (!Array.isArray(data) || data.length === 0) {
      console.warn('[PaletteService] Invalid response shape, using fallback palettes');
      return FALLBACK_PALETTES;
    }

    // Normalize: ensure renderImage is a usable URL
    const palettes: ColorPalette[] = data.map((p: ColorPalette) => ({
      ...p,
      // Backend returns full GCS URLs for renderImage; use as-is
      renderImage: p.renderImage ?? undefined,
    }));

    cachedPalettes = palettes;
    return cachedPalettes;
  } catch (err) {
    console.warn('[PaletteService] Failed to fetch palettes, using fallback:', err);
    return FALLBACK_PALETTES;
  }
}
